/**
 * The two unauthenticated login endpoints.
 *
 * Kept apart from the Bearer-authenticated services on purpose. Those answer a
 * 401 by clearing the session and hard-redirecting to /login (sessionExpiry's
 * raiseSessionExpired); doing that here would send the login page to itself in
 * a loop. Body reading is shared via httpResponse; status mapping is not, and
 * that is the whole reason these are siblings rather than one module.
 *
 * Results are tagged rather than thrown, because a 428 is not an error — it is
 * a second step. Modelling it as an exception would mean the happy path went
 * through a catch block.
 */

import { API_BASE_URL } from './api';
import { readBody, messageFrom } from './httpResponse';
import {
  resolveExpiryDeadline,
  toClientClockSeconds,
  CHALLENGE_FALLBACK_WINDOW_MS,
} from './expiryWindow';

// Not getAuthHeaders: these two endpoints are the ones that *issue* the token,
// so there is nothing to authorise with yet.
const JSON_HEADERS = { 'Content-Type': 'application/json' };

const GENERIC_FAILURE = 'Something went wrong. Please try again.';
const TOO_MANY_ATTEMPTS = 'Too many attempts. Please wait a few minutes and try again.';

/**
 * Copy owned here rather than taken from the server.
 *
 * /login/mfa answers 401 with one byte-for-byte identical body for five
 * distinct causes — wrong code, expired challenge, forged token, no account
 * for the verified factor, and an unexpected failure — and there is a
 * server-side test pinning that invariance. Rendering the server's message
 * would therefore be safe but useless: it cannot say anything specific, by
 * design. Ours at least names the two things a user can do about it.
 */
const CHALLENGE_REJECTED =
  "That code wasn't accepted. Check your authenticator app and try again, or start over to request a new sign-in.";

const buildSession = (json) => ({
  userId: json.userId,
  username: json.username,
  accessToken: json.accessToken,
  refreshToken: json.refreshToken,
  expiresIn: json.expiresIn,
});

/**
 * Turn a 428 body into a challenge, or return null if it is not one we can act
 * on. Validating before promoting matters because the alternative is rendering
 * a code-entry form with no token to submit it against — a dead end the user
 * cannot get out of without reloading.
 */
const toChallenge = (json, receivedAt, serverDateHeader) => {
  if (!json || json.error !== 'mfa_required') return null;
  if (typeof json.mfa_token !== 'string' || !json.mfa_token) return null;

  const factors = Array.isArray(json.factors)
    ? json.factors.filter((factor) => factor && typeof factor.id === 'string' && factor.id)
    : [];
  if (factors.length === 0) return null;

  // Prefer totp explicitly rather than by position. Only totp exists today, so
  // factors[0] would be right by accident — and would quietly stop being right
  // the first time the server returns anything ahead of it.
  const preferred = factors.find((factor) => factor.type === 'totp') || factors[0];

  return {
    status: 'mfa_required',
    mfaToken: json.mfa_token,
    factors,
    factorId: preferred.id,
    // Derived here because this is the moment the challenge was actually
    // received; the deadline is measured against the client's own clock from
    // this point on, so a skewed clock shifts the window rather than
    // collapsing it. See expiryWindow.
    //
    // And the skew is cancelled rather than merely tolerated, using the `Date`
    // header from this same response — without that step a browser running slow
    // gets a window longer than the challenge it is counting down, and the
    // countdown stops being evidence of anything. Costs nothing when the header
    // has been stripped: the value passes through untouched.
    expiresAt: resolveExpiryDeadline(
      toClientClockSeconds(json.expires_at, serverDateHeader, receivedAt),
      CHALLENGE_FALLBACK_WINDOW_MS,
      receivedAt,
    ),
  };
};

/**
 * Sign in with a username and password.
 *
 * @returns {Promise<
 *   {status: 'success', session: object} |
 *   {status: 'mfa_required', mfaToken: string, factors: object[], factorId: string, expiresAt: number} |
 *   {status: 'error', message: string}
 * >}
 */
export const login = async ({ username, password }) => {
  const response = await fetch(`${API_BASE_URL}/login`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ username, password }),
  });
  const receivedAt = Date.now();

  const body = await readBody(response);

  if (response.ok) {
    return body.json
      ? { status: 'success', session: buildSession(body.json) }
      : { status: 'error', message: GENERIC_FAILURE };
  }

  if (response.status === 428) {
    const challenge = toChallenge(body.json, receivedAt, response.headers?.get?.('Date'));
    return (
      challenge || {
        status: 'error',
        message: "We couldn't start two-factor verification. Please try again.",
      }
    );
  }

  // These three answer FistoneApiError envelopes like the rest of auth_ctrl.go,
  // and the copy shown is still ours rather than theirs — see each case. The
  // statuses not listed fall through to messageFrom below, which is where the
  // server does get to speak for itself.
  if (response.status === 401) {
    // Not the server's text, which is written for an API consumer, and not
    // narrowed to say which half was wrong.
    return { status: 'error', message: 'Invalid username or password' };
  }

  if (response.status === 403) {
    return {
      status: 'error',
      message: "Your account hasn't been confirmed yet. Check your email for the confirmation link.",
    };
  }

  if (response.status === 429) {
    return { status: 'error', message: TOO_MANY_ATTEMPTS };
  }

  return { status: 'error', message: messageFrom(body, GENERIC_FAILURE) };
};

/**
 * Answer an outstanding MFA challenge.
 *
 * Every failure here uses copy owned by this module. The server's bodies are
 * FistoneApiError envelopes now, but their text is either deliberately
 * invariant (401, see above) or written for a developer reading a log rather
 * than a person looking at a form.
 *
 * @returns {Promise<{status: 'success', session: object} | {status: 'error', message: string}>}
 */
export const submitMfaCode = async ({ mfaToken, factorId, code }) => {
  const response = await fetch(`${API_BASE_URL}/login/mfa`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ mfa_token: mfaToken, factor_id: factorId, code }),
  });

  const body = await readBody(response);

  if (response.ok) {
    return body.json
      ? { status: 'success', session: buildSession(body.json) }
      : { status: 'error', message: GENERIC_FAILURE };
  }

  if (response.status === 401) {
    return { status: 'error', message: CHALLENGE_REJECTED };
  }

  if (response.status === 400) {
    // The only way a real user reaches this is a missing or malformed code;
    // the server's "The request could not be read." describes the request, not
    // anything the user can act on.
    return { status: 'error', message: 'Enter the 6-digit code from your authenticator app.' };
  }

  if (response.status === 429) {
    return { status: 'error', message: TOO_MANY_ATTEMPTS };
  }

  return { status: 'error', message: messageFrom(body, GENERIC_FAILURE) };
};
